export type ParseTimeResult =
  | { ok: true; seconds: number }
  | { ok: false; error: string }

/**
 * Accept mm:ss or hh:mm:ss with optional fractional seconds.
 * Seconds/minutes components must be below 60.
 */
export function parseTimestamp(input: string): ParseTimeResult {
  const trimmed = input.trim()
  if (!trimmed) {
    return { ok: false, error: 'Timestamp is empty.' }
  }

  const parts = trimmed.split(':')
  if (parts.length < 2 || parts.length > 3) {
    return { ok: false, error: 'Use mm:ss or hh:mm:ss.' }
  }

  const nums = parts.map((p) => Number(p))
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) {
    return { ok: false, error: 'Timestamp must be a non-negative number.' }
  }

  if (parts.length === 2) {
    const [minutes, seconds] = nums
    if (seconds! >= 60) {
      return { ok: false, error: 'Seconds must be below 60.' }
    }
    return { ok: true, seconds: minutes! * 60 + seconds! }
  }

  const [hours, minutes, seconds] = nums
  if (minutes! >= 60) {
    return { ok: false, error: 'Minutes must be below 60.' }
  }
  if (seconds! >= 60) {
    return { ok: false, error: 'Seconds must be below 60.' }
  }
  return { ok: true, seconds: hours! * 3600 + minutes! * 60 + seconds! }
}

export function formatTimestamp(totalSeconds: number, includeHours = false): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return includeHours ? '00:00:00' : '00:00'
  }

  const whole = Math.floor(totalSeconds)
  const frac = totalSeconds - whole
  const hours = Math.floor(whole / 3600)
  const minutes = Math.floor((whole % 3600) / 60)
  const seconds = whole % 60

  const secStr =
    frac > 0.0005
      ? `${String(seconds).padStart(2, '0')}${(frac).toFixed(1).slice(1)}`
      : String(seconds).padStart(2, '0')

  if (includeHours || hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${secStr}`
  }
  return `${String(minutes).padStart(2, '0')}:${secStr}`
}
